import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, take, tap } from 'rxjs';
import { AppProfileService } from '../../services/profile.service';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { UserNameUpdateRequest } from '../../common/UserNameDto';
import { FormRowComponent } from '../form-row/form-row.component';

/**
 * Self-service editing of the signed-in user's OWN name (given_name / middle_name /
 * family_name claims) over the BFF (backend #571). Deliberately separate from the
 * customer forms: the Customer's name fields are the vendor's legal-entity identity,
 * while this form edits the person behind the login. An emptied middle name removes
 * the middle_name claim.
 */
@Component({
    imports: [ReactiveFormsModule, CommonModule, AsyncClickDirective, FormRowComponent],
    selector: 'app-user-name-edit',
    templateUrl: './user-name-edit.component.html',
    styleUrls: ['./user-name-edit.component.css']
})
export class UserNameEditComponent implements OnInit {
  private profileService = inject(AppProfileService);
  private callApiService = inject(CallApiService);

  public nameForm = new FormGroup({
    firstName: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    middleName: new FormControl('', [Validators.maxLength(100)]),
    lastName: new FormControl('', [Validators.required, Validators.maxLength(100)]),
  });

  ngOnInit(): void {
    this.profileService.currentProfile$.pipe(take(1)).subscribe((profile) => {
      this.nameForm.patchValue({
        firstName: profile?.user?.firstName ?? '',
        middleName: profile?.user?.middleName ?? '',
        lastName: profile?.user?.lastName ?? '',
      });
    });
  }

  // Needs the form value → returns a factory: [asyncClick]="save(nameForm.value)".
  save = (formValue: any) => (): Observable<unknown> => {
    const request: UserNameUpdateRequest = {
      firstName: (formValue.firstName ?? '').trim(),
      middleName: (formValue.middleName ?? '').trim(),
      lastName: (formValue.lastName ?? '').trim(),
    };

    return this.callApiService
      .callWithToasts$(
        this.profileService.updateUserName(request),
        $localize`Ihr Name wurde gespeichert`,
        $localize`Ihr Name konnte nicht gespeichert werden`,
      )
      // Avatar and profile page bind the BFF profile — refresh so the new name
      // shows immediately, no re-login needed.
      .pipe(tap(() => this.profileService.refreshCurrentProfile()));
  };
}
